import { useParams } from "react-router-dom"
import { useState } from "react"
import Layout from "@/components/Layout/Layout"
import { Card, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { LoadingScreen } from "@/components/ui/loading-spinner"
import { useTournamentInfo, useTournamentOrganizers } from "@/graphql/hooks/useTournamentDetail"
import { Edit } from "lucide-react"
import { TournamentNavigation } from "@/components/TournamentNavigation"
import { useAuthContext } from "@/contexts/AuthContext"
import { TournamentEditModal } from "@/components/TournamentEditModal"
import { CoverUpload } from "@/components/ui/cover-upload"

import { getTournamentImage, getExtract } from "./utils"
import { ListPlayers } from "./Sidebar/ListPlayers"
import { StoreInfo } from "./Sidebar/StoreInfo"
import { SeasonInfo } from "./Sidebar/SeasonInfo"
import { TournamentInfo } from "./TournamentInfo"
import { TournamentCasualInfo } from "./TournamentCasualInfo"

/**
 * Vista componente Torneo Detalle
 * @returns
 */
const TournamentDetail = () => {
  const { tournamentId } = useParams<{ tournamentId: string }>()
  const { userInfo, loading: authLoading, isAdmin, isAuthenticated } = useAuthContext()
  const { tournament, related, loading, error, refetch } = useTournamentInfo(Number(tournamentId))
  const { organizers } = useTournamentOrganizers(Number(tournamentId), isAuthenticated)

  const [isEditModalOpen, setIsEditModalOpen] = useState(false)

  if (loading) {
    return (
      <Layout>
        <LoadingScreen message="Cargando detalle del torneo..." />
      </Layout>
    )
  }

  if (error || !tournament) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8">
          <Card>
            <CardContent className="p-8 text-center">
              <h2 className="text-2xl font-bold text-muted-foreground mb-4">
                Torneo no encontrado
              </h2>
              <p className="text-muted-foreground">
                No se pudo cargar la información del torneo solicitado.
              </p>
            </CardContent>
          </Card>
        </div>
      </Layout>
    )
  }

  // Check authorization
  const canAccess = () => {
    if (authLoading || !userInfo || !tournament) return false

    // Check if user is admin (we'll assume admins have categoryId 1 for now)
    if (isAdmin) {
      return true
    }

    // Check if user owns the store that owns this tournament
    const isStoreOwner = userInfo.User?.Person?.Store?.id === tournament.storeId

    const isOrganizer = organizers?.some(({ personId }) => personId === userInfo?.User?.Person?.id)

    return isAdmin || isStoreOwner || isOrganizer
  }

  return (
    <Layout>
      <div className="container mx-auto p-2">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Main Tournament Info */}
          <div className="lg:col-span-2 space-y-6">
            <Card className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 overflow-hidden">
              {/* Cover image */}
              <div className="relative">
                {isAdmin ? (
                  <CoverUpload
                    src={getTournamentImage(tournament)}
                    name={tournament.name || "Torneo"}
                    personId={userInfo?.User?.Person?.id || 0}
                    tournamentId={tournament.id}
                    type="tournament.image"
                    className="dark:border-gray-700"
                  />
                ) : (
                  <img
                    src={getTournamentImage(tournament)}
                    alt={tournament.name}
                    className="w-full object-cover"
                  />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <h1 className="text-xl sm:text-2xl font-bold text-white leading-tight drop-shadow">
                    {tournament.name}
                  </h1>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {tournament.TournamentType?.name && (
                      <Badge className="text-[10px] px-1.5 py-0 bg-white/20 text-white border-white/30 backdrop-blur-sm">
                        {tournament.TournamentType.name}
                      </Badge>
                    )}
                    {tournament.Format?.name && (
                      <Badge className="text-[10px] px-1.5 py-0 bg-white/20 text-white border-white/30 backdrop-blur-sm">
                        {tournament.Format.name}
                      </Badge>
                    )}
                  </div>
                </div>
              </div>

              {/* Description + actions */}
              {(getExtract(tournament.description) || canAccess()) && (
                <CardContent className="px-4 py-3 flex items-start justify-between gap-3">
                  {getExtract(tournament.description) && (
                    <p className="text-sm text-muted-foreground dark:text-gray-300 break-words flex-1">
                      {getExtract(tournament.description)}
                    </p>
                  )}
                  {canAccess() && tournament.statusId !== 6 && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="shrink-0"
                      onClick={() => setIsEditModalOpen(true)}
                    >
                      <Edit className="h-3.5 w-3.5 mr-1.5" />
                      Editar
                    </Button>
                  )}
                </CardContent>
              )}
            </Card>

            {canAccess() && (
              <TournamentNavigation
                tournament={tournament}
                tournamentName={tournament.name || ""}
                currentPage="detail"
                tournamentStatusId={tournament.statusId}
                showRounds={false}
              />
            )}

            <TournamentCasualInfo tournament={tournament} />

            {/* Tournament Details */}
            <TournamentInfo tournament={tournament} />

            {/* Description */}
            {tournament.description && (
              <Card className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 overflow-hidden">
                <CardContent className="p-0">
                  <div className="flex items-center gap-2 px-4 pt-4 pb-3 border-b border-muted/40 dark:border-gray-700">
                    <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                      Descripción del evento
                    </span>
                  </div>
                  <p className="px-4 py-3 text-sm text-muted-foreground dark:text-gray-300 break-words leading-relaxed">
                    {tournament.description}
                  </p>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Game & Season Info */}
            <SeasonInfo game={tournament.Game} season={tournament.Season} />

            <StoreInfo store={tournament?.Store} />

            {/* Tournament Players */}
            <ListPlayers />
          </div>
        </div>
      </div>

      {/* Edit Tournament Modal */}
      {tournament && isAuthenticated && (
        <TournamentEditModal
          open={isEditModalOpen}
          onOpenChange={setIsEditModalOpen}
          tournament={tournament}
          related={related}
          onSuccess={() => {
            refetch()
          }}
        />
      )}
    </Layout>
  )
}

export default TournamentDetail
